import React, { useState, useMemo } from 'react';
import { Tabs, Typography, Button, Input, Form, Collapse, Tooltip, Row, Col, Space } from 'antd';
import CustomDrawer from 'components/shared-components/CustomDrawer';
import { SwapOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { getNodeMeta, NODE_CATEGORIES, NODE_TYPE_LIST } from '../constants/nodeTypes';
import { renderNodeConfig } from './nodeConfigs/NodeConfigForms';
import NodeSelect from './NodeSelect';
import { TYPES_WITH_OWN_ROUTING, getOutgoingEvent } from '../hooks/useFlowEditor';

const { Text, Title } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const NodeConfigDrawer = ({
  open,
  node,
  nodeOptions,
  placeholders = [],
  varOptions = [],
  definition = null,
  currentFlowId = null,
  onClose,
  onConfigChange,
  onSaveNode,
  onChangeNodeType,
}) => {
  const [activeTab, setActiveTab] = useState('config');
  const [notes, setNotes] = useState('');
  const [pickingType, setPickingType] = useState(false);
  const [typeSearch, setTypeSearch] = useState('');

  // Compute the current "next step" target for this node from edges
  const showNextStep = node && !TYPES_WITH_OWN_ROUTING.has(node?.type) && !node?.type?.startsWith('trigger.');
  const currentNextStep = useMemo(() => {
    if (!showNextStep || !definition?.edges || !node) return undefined;
    const event = getOutgoingEvent(node.type);
    const edge = definition.edges.find((e) => e.from === node.id && e.event === event);
    return edge?.to || undefined;
  }, [showNextStep, definition?.edges, node]);

  // ─── All hooks must be called before any early return ─────────────────────
  const filteredTypes = useMemo(() => {
    const q = typeSearch.toLowerCase();
    return NODE_TYPE_LIST.filter(
      (n) =>
        !n.type.startsWith('trigger.') &&
        (!q || n.label.toLowerCase().includes(q) || n.type.toLowerCase().includes(q))
    );
  }, [typeSearch]);

  const categories = NODE_CATEGORIES.filter((c) => c.key !== 'triggers');

  if (!node) return null;

  const meta = getNodeMeta(node.type);
  const Icon = meta.icon;
  const isTrigger = node.type.startsWith('trigger.');

  const handleChange = (patch) => {
    onConfigChange?.(node.id, patch);
  };

  const handlePickType = (newType) => {
    if (newType === node.type) { setPickingType(false); return; }
    onChangeNodeType?.(node.id, newType);
    setPickingType(false);
    setTypeSearch('');
  };

  if (pickingType) {
    const pickerTitle = (
      <Row gutter={[12, 12]} style={{ width: '100%', margin: 0 }} align="middle">
        <Col xs={9} sm={12} md={12} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 0 }}>
          <Button
            size="small"
            icon={<ArrowLeftOutlined />}
            onClick={() => { setPickingType(false); setTypeSearch(''); }}
          />
          <span style={{ fontSize: 16, fontWeight: 600, whiteSpace: 'nowrap' }}>Change type</span>
        </Col>
        <Col xs={15} sm={12} md={12} style={{ display: 'flex', justifyContent: 'flex-end', padding: 0 }}>
          <Input
            prefix={<span style={{ color: '#bfbfbf' }}>🔍</span>}
            placeholder="Search types..."
            value={typeSearch}
            onChange={(e) => setTypeSearch(e.target.value)}
            allowClear
            autoFocus
            style={{ width: '100%', maxWidth: 220 }}
          />
        </Col>
      </Row>
    );

    return (
      <CustomDrawer
        title={pickerTitle}
        width={800}
        open={open}
        onClose={onClose}
        mask={false}
        closable={false}
        styles={{ body: { padding: '16px 12px' } }}
      >
        <Collapse defaultActiveKey={categories.map((c) => c.key)} size="small">
          {categories.map((cat) => {
            const items = filteredTypes.filter((n) => n.category === cat.key);
            if (!items.length) return null;
            return (
              <Panel header={cat.label} key={cat.key}>
                <Row gutter={[10, 10]}>
                  {items.map((n) => {
                    const NIcon = n.icon;
                    const isCurrent = n.type === node.type;
                    return (
                      <Col key={n.type} xs={24} sm={12} md={8}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handlePickType(n.type)}
                          onKeyDown={(e) => e.key === 'Enter' && handlePickType(n.type)}
                          style={{
                            height: '100%',
                            padding: '10px 12px',
                            border: `1px solid ${isCurrent ? n.color : '#f0f0f0'}`,
                            borderRadius: 8,
                            cursor: 'pointer',
                            borderLeft: `4px solid ${n.color}`,
                            background: isCurrent ? '#fafffe' : '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            transition: 'all 0.15s',
                          }}
                        >
                          <strong style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {NIcon && <NIcon style={{ color: n.color, flexShrink: 0 }} />}
                            {n.label}
                            {isCurrent && (
                              <Text type="secondary" style={{ fontWeight: 400, fontSize: 11, marginLeft: 4 }}>
                                (current)
                              </Text>
                            )}
                          </strong>
                          <div style={{ fontSize: 11, color: '#8c8c8c', lineHeight: '1.4' }}>{n.subtitle}</div>
                        </div>
                      </Col>
                    );
                  })}
                </Row>
              </Panel>
            );
          })}
        </Collapse>
      </CustomDrawer>
    );
  }

  // ─── Normal config drawer ─────────────────────────────────────────────────
  return (
    <CustomDrawer
      title={null}
      width={'50vw'}
      open={open}
      onClose={onClose}
      mask={true}
      maskClosable={true}
      styles={{
        body: {
          paddingTop: 16,
          paddingBottom: 80,
          overflow: 'auto',
        },
      }}
    >
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <SpaceIcon Icon={Icon} color={meta.color} />
          <Title level={5} style={{ margin: 0, flex: 1 }}>
            {meta.label}
          </Title>
          {!isTrigger && (
            <Tooltip title="Change node type">
              <Button
                size="small"
                icon={<SwapOutlined />}
                onClick={() => setPickingType(true)}
              />
            </Tooltip>
          )}
        </div>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          {meta.subtitle}
        </Text>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'config',
            label: 'Node Configuration',
            children: (
              <Form layout="vertical">
                <Form.Item
                  label="Label / note"
                  extra="Optional — shown in the flow list so you can identify this step at a glance."
                  style={{ marginBottom: 20 }}
                >
                  <Input
                    placeholder="e.g. Ask for customer name"
                    value={node.config?._label ?? ''}
                    onChange={(e) => handleChange({ _label: e.target.value })}
                    allowClear
                  />
                </Form.Item>
                {renderNodeConfig(node.type, {
                  config: node.config || {},
                  onChange: handleChange,
                  nodeOptions,
                  placeholders,
                  varOptions,
                  nodeId: node.id,
                  definition,
                  currentFlowId,
                })}

                {/* Universal "Next Step" field for non-branching nodes */}
                {showNextStep && node.type !== 'flow.end' && (
                  <Form.Item
                    label="Next Step"
                    extra="Which node runs after this one. Changing this auto-reconnects the flow chain."
                    style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f0f0' }}
                  >
                    <NodeSelect
                      value={currentNextStep}
                      onChange={(val) => handleChange({ _next_step: val || null })}
                      placeholder="Select next step..."
                    />
                  </Form.Item>
                )}
              </Form>
            ),
          },
          {
            key: 'info',
            label: 'Node Info',
            children: (
              <>
                <Form.Item label="Node ID">
                  <Input value={node.id} disabled />
                </Form.Item>
                <Form.Item label="Type">
                  <Input value={node.type} disabled />
                </Form.Item>
                <Form.Item label="Notes (optional)">
                  <TextArea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Form.Item>
              </>
            ),
          },
        ]}
      />

      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '12px 24px',
          background: '#fff',
          borderTop: '1px solid #f0f0f0',
          display: 'flex',
          gap: 8,
          zIndex: 10,
        }}
      >
        {/* {node.type === 'integration.api' && (
          <Button style={{ flex: 1 }}>Test API</Button>
        )} */}
        <Button type="primary" style={{ flex: 1 }} onClick={onSaveNode}>
          Save node
        </Button>
      </div>

      {/* <!-- Bottom padding for the drawer --> */}
      <div style={{ height: 50 }} />
    </CustomDrawer>
  );
};

function SpaceIcon({ Icon, color }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        width: 36,
        height: 36,
        borderRadius: 8,
        background: color,
        color: '#fff',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {Icon && <Icon />}
    </span>
  );
}

export default NodeConfigDrawer;
